/**
 * SellerDashboardLayout - Seller Dashboard Layout Component
 *
 * A presentational layout component for the seller dashboard in the marketplace module.
 * Features:
 * - Responsive grid layout for stats cards (mobile-first)
 * - Sidebar navigation with configurable items
 * - Breadcrumb navigation support
 * - Loading states with shadcn/ui Skeleton components
 * - Error handling with proper error display
 * - Type-safe props interface
 *
 * IMPORTANT: This is a presentational component - no API calls.
 * All data must be passed via props.
 */

import React, { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Package,
  MessageSquare,
  PlusCircle,
  ChevronRight,
  Menu,
  X,
  AlertCircle,
  RefreshCcw,
  LucideIcon,
} from 'lucide-react';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Navigation item for sidebar
 */
export interface SellerNavigationItem {
  /** Unique identifier for the navigation item */
  id: string;
  /** Display label for the navigation item */
  label: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Route path for navigation */
  path: string;
  /** Optional badge count to display */
  badge?: number;
}

/**
 * Breadcrumb item for navigation path
 */
export interface SellerBreadcrumbItem {
  /** Display label for the breadcrumb */
  label: string;
  /** Optional route path (clickable if provided) */
  path?: string;
}

/**
 * Stats card data structure
 */
export interface SellerStatCard {
  /** Unique identifier for the stat card */
  id: string;
  /** Title of the stat */
  title: string;
  /** Numeric or string value to display */
  value: string | number;
  /** Optional description or subtitle */
  description?: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Optional trend indicator (positive/negative/neutral) */
  trend?: 'up' | 'down' | 'neutral';
  /** Optional trend percentage value */
  trendValue?: string;
  /** Icon background color class */
  iconBgColor?: string;
  /** Icon color class */
  iconColor?: string;
}

/**
 * Error state structure
 */
export interface SellerDashboardError {
  /** Error message to display */
  message: string;
  /** Optional retry callback */
  onRetry?: () => void;
}

/**
 * Props interface for SellerDashboardLayout component
 */
export interface SellerDashboardLayoutProps {
  /** Main content to render in the content area */
  children: ReactNode;
  /** Navigation items for the sidebar */
  navigationItems?: SellerNavigationItem[];
  /** Breadcrumb items for navigation path */
  breadcrumbs?: SellerBreadcrumbItem[];
  /** Stats cards data to display */
  statsCards?: SellerStatCard[];
  /** Loading state for the entire layout */
  isLoading?: boolean;
  /** Loading state specifically for stats cards */
  isStatsLoading?: boolean;
  /** Error state for the layout */
  error?: SellerDashboardError | null;
  /** Current active navigation item ID */
  activeNavItemId?: string;
  /** Page title to display in header */
  pageTitle?: string;
  /** Page subtitle/description */
  pageSubtitle?: string;
  /** Custom className for main content area */
  contentClassName?: string;
  /** Callback when navigation item is clicked */
  onNavigate?: (item: SellerNavigationItem) => void;
  /** Hide the stats cards section */
  hideStats?: boolean;
  /** Hide the sidebar navigation */
  hideSidebar?: boolean;
}

// ============================================================================
// Default Navigation Items
// ============================================================================

const DEFAULT_NAVIGATION_ITEMS: SellerNavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/marketplace/seller/dashboard',
  },
  {
    id: 'products',
    label: 'My Products',
    icon: Package,
    path: '/marketplace/seller/products',
  },
  {
    id: 'inquiries',
    label: 'Inquiries',
    icon: MessageSquare,
    path: '/marketplace/seller/inquiries',
  },
  {
    id: 'add-product',
    label: 'Add Product',
    icon: PlusCircle,
    path: '/marketplace/seller/products/add',
  },
];

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * Skeleton loader for stats cards
 */
const StatsCardSkeleton: React.FC = () => (
  <Card className="bg-white">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-8 w-8 rounded-lg" />
    </CardHeader>
    <CardContent>
      <Skeleton className="h-8 w-20 mb-1" />
      <Skeleton className="h-3 w-32" />
    </CardContent>
  </Card>
);

/**
 * Single stats card component
 */
const StatsCard: React.FC<{ card: SellerStatCard }> = ({ card }) => {
  const Icon = card.icon;
  const bgColor = card.iconBgColor || 'bg-blue-100';
  const iconColor = card.iconColor || 'text-blue-600';

  return (
    <Card className="bg-white hover:shadow-md transition-shadow duration-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">
          {card.title}
        </CardTitle>
        <div className={cn('p-2 rounded-lg', bgColor)}>
          <Icon className={cn('h-4 w-4', iconColor)} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-gray-900">{card.value}</div>
        {card.description && (
          <p className="text-xs text-gray-500 mt-1">{card.description}</p>
        )}
        {card.trend && card.trendValue && (
          <p
            className={cn('text-xs mt-1 font-medium', {
              'text-green-600': card.trend === 'up',
              'text-red-600': card.trend === 'down',
              'text-gray-500': card.trend === 'neutral',
            })}
          >
            {card.trend === 'up' && '+'}
            {card.trend === 'down' && '-'}
            {card.trendValue}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Sidebar navigation component
 */
const SidebarNavigation: React.FC<{
  items: SellerNavigationItem[];
  activeItemId?: string;
  onNavigate?: (item: SellerNavigationItem) => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}> = ({ items, activeItemId, onNavigate, isMobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleItemClick = (item: SellerNavigationItem) => {
    if (onNavigate) {
      onNavigate(item);
    } else {
      navigate(item.path);
    }
    onMobileClose();
  };

  const isItemActive = (item: SellerNavigationItem): boolean => {
    if (activeItemId) {
      return item.id === activeItemId;
    }
    return location.pathname === item.path;
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 w-64 h-screen bg-white border-r border-gray-200 z-50',
          'transform transition-transform duration-300 ease-in-out',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0'
        )}
        aria-label="Seller portal sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="h-16 border-b border-gray-200 flex items-center justify-between px-4">
            <h2 className="text-lg font-bold text-gray-900">Seller Portal</h2>
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden p-1"
              onClick={onMobileClose}
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close menu</span>
            </Button>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 p-4 overflow-y-auto" aria-label="Main navigation">
            <div className="space-y-2">
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = isItemActive(item);

                return (
                  <Button
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    variant={isActive ? 'default' : 'ghost'}
                    className={cn(
                      'w-full justify-start h-10 font-medium text-sm transition-all duration-200',
                      isActive
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    )}
                  >
                    <Icon
                      className={cn(
                        'mr-3 h-4 w-4 flex-shrink-0',
                        isActive ? 'text-white' : 'text-gray-500'
                      )}
                    />
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.badge !== undefined && item.badge > 0 && (
                      <span
                        className={cn(
                          'ml-auto text-xs font-medium px-2 py-0.5 rounded-full',
                          isActive
                            ? 'bg-white/20 text-white'
                            : 'bg-blue-100 text-blue-600'
                        )}
                      >
                        {item.badge}
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center text-sm text-gray-500">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2" />
              Online
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

/**
 * Breadcrumb navigation component
 */
const BreadcrumbNav: React.FC<{
  items: SellerBreadcrumbItem[];
}> = ({ items }) => {
  const navigate = useNavigate();

  if (items.length === 0) return null;

  return (
    <nav className="flex items-center space-x-1 text-sm text-gray-600 mb-4" aria-label="Breadcrumb">
      {items.map((item, index) => (
        <div key={index} className="flex items-center">
          {index > 0 && (
            <ChevronRight className="h-4 w-4 mx-1 text-gray-400 flex-shrink-0" />
          )}
          {item.path ? (
            <button
              onClick={() => navigate(item.path!)}
              className="hover:text-blue-600 transition-colors font-medium hover:underline"
            >
              {item.label}
            </button>
          ) : (
            <span
              className={cn(
                index === items.length - 1
                  ? 'text-gray-900 font-medium'
                  : 'text-gray-600'
              )}
            >
              {item.label}
            </span>
          )}
        </div>
      ))}
    </nav>
  );
};

/**
 * Error display component
 */
const ErrorDisplay: React.FC<{
  error: SellerDashboardError;
}> = ({ error }) => (
  <Alert variant="destructive" className="mb-6">
    <AlertCircle className="h-4 w-4" />
    <AlertTitle>Error</AlertTitle>
    <AlertDescription className="mt-2">
      <p>{error.message}</p>
      {error.onRetry && (
        <Button
          variant="outline"
          size="sm"
          onClick={error.onRetry}
          className="mt-3"
        >
          <RefreshCcw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      )}
    </AlertDescription>
  </Alert>
);

// ============================================================================
// Main Component
// ============================================================================

/**
 * SellerDashboardLayout - Main layout component for seller dashboard
 *
 * @example
 * ```tsx
 * <SellerDashboardLayout
 *   breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Dashboard' }]}
 *   statsCards={statsData}
 *   isStatsLoading={isLoading}
 *   activeNavItemId="dashboard"
 * >
 *   <YourPageContent />
 * </SellerDashboardLayout>
 * ```
 */
export const SellerDashboardLayout: React.FC<SellerDashboardLayoutProps> = ({
  children,
  navigationItems = DEFAULT_NAVIGATION_ITEMS,
  breadcrumbs = [],
  statsCards = [],
  isLoading = false,
  isStatsLoading = false,
  error = null,
  activeNavItemId,
  pageTitle,
  pageSubtitle,
  contentClassName,
  onNavigate,
  hideStats = false,
  hideSidebar = false,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  // Full page loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Sidebar Skeleton */}
        {!hideSidebar && (
          <aside className="hidden lg:block fixed left-0 top-0 w-64 h-screen bg-white border-r border-gray-200">
            <div className="p-4 space-y-4">
              <Skeleton className="h-8 w-32" />
              <div className="space-y-2 mt-8">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            </div>
          </aside>
        )}

        {/* Main Content Skeleton */}
        <div className={cn('min-h-screen', !hideSidebar && 'lg:ml-64')}>
          <div className="p-6">
            <Skeleton className="h-6 w-48 mb-4" />
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-96 mb-6" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[...Array(4)].map((_, i) => (
                <StatsCardSkeleton key={i} />
              ))}
            </div>
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar Navigation */}
      {!hideSidebar && (
        <SidebarNavigation
          items={navigationItems}
          activeItemId={activeNavItemId}
          onNavigate={onNavigate}
          isMobileOpen={isMobileMenuOpen}
          onMobileClose={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main Content Area */}
      <div className={cn('min-h-screen', !hideSidebar && 'lg:ml-64')}>
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2"
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">Open menu</span>
            </Button>
            <div className="text-lg font-semibold text-gray-900">
              {pageTitle || 'Seller Portal'}
            </div>
            <div className="w-10" /> {/* Spacer for centering */}
          </div>
        </header>

        {/* Page Content */}
        <main className={cn('p-4 md:p-6', contentClassName)} aria-label="Main content">
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && <BreadcrumbNav items={breadcrumbs} />}

          {/* Page Header */}
          {(pageTitle || pageSubtitle) && (
            <div className="mb-6">
              {pageTitle && (
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {pageTitle}
                </h1>
              )}
              {pageSubtitle && (
                <p className="mt-1 text-sm md:text-base text-gray-600">
                  {pageSubtitle}
                </p>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && <ErrorDisplay error={error} />}

          {/* Stats Cards Section */}
          {!hideStats && (
            <section className="mb-6" aria-label="Statistics">
              {isStatsLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <StatsCardSkeleton key={i} />
                  ))}
                </div>
              ) : statsCards.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {statsCards.map((card) => (
                    <StatsCard key={card.id} card={card} />
                  ))}
                </div>
              ) : null}
            </section>
          )}

          {/* Main Page Content */}
          <section className="w-full" aria-label="Page content">
            {children}
          </section>
        </main>
      </div>
    </div>
  );
};

// ============================================================================
// Exports
// ============================================================================

export default SellerDashboardLayout;
